import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';
import { fetchUserProfileSummary } from '../services/userService';
import { DEFAULT_RUNTIME_SETTINGS, getRuntimeSettings } from '../services/runtimeSettings';
import PostTradeReviewPanel from '../components/event-day/PostTradeReviewPanel';
import TradeSessionPanel from '../components/event-day/TradeSessionPanel';
import WaitingForAssignmentPanel from '../components/event-day/WaitingForAssignmentPanel';
import CompletedAllTradesPanel from '../components/event-day/CompletedAllTradesPanel';

const profileSummaryCache = new Map<string, Promise<any>>();

const getCachedUserProfileSummary = (userId: string) => {
  if (!userId) return Promise.resolve(null);
  if (!profileSummaryCache.has(userId)) {
    profileSummaryCache.set(userId, fetchUserProfileSummary(userId).catch((error) => {
      profileSummaryCache.delete(userId);
      throw error;
    }));
  }
  return profileSummaryCache.get(userId)!;
};
const getCheckInPollIntervalMs = (eligibility: any, locationState: any, runtimeSettings = DEFAULT_RUNTIME_SETTINGS) => {
  const polling = runtimeSettings.eventDayPolling;
  if (locationState?.session?.status === 'active' || locationState?.state === 'active-session') return polling.participantActivePollMs;
  if (locationState?.location?.id || locationState?.session?.id || eligibility?.state === 'already-checked-in') return polling.participantAssignedPollMs;
  if (eligibility?.state === 'eligible') return polling.participantWaitingPollMs;
  return polling.participantIdlePollMs;
};

const EventCheckInPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { initialized, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState<number | null>(5);
  const [reviewComment, setReviewComment] = useState('');
  const [runtimeSettings, setRuntimeSettings] = useState(DEFAULT_RUNTIME_SETTINGS);
  const [eventDayState, setEventDayState] = useState<any>({
    eligibility: null,
    locationState: null,
    reviews: [],
    reviewPrompt: {
      sessionId: '',
      session: null,
      assignment: null,
      targetUserId: '',
      targetDisplayName: '',
      submitted: false,
    },
  });

  const pollingTimeoutRef = useRef<number | null>(null);
  const refreshInFlightRef = useRef(false);
  const initialLoadStartedRef = useRef(false);
  const eventDayStateRef = useRef(eventDayState);
  const runtimeSettingsRef = useRef(runtimeSettings);

  useEffect(() => {
    eventDayStateRef.current = eventDayState;
  }, [eventDayState]);

  useEffect(() => {
    runtimeSettingsRef.current = runtimeSettings;
  }, [runtimeSettings]);

  useEffect(() => {
    let cancelled = false;
    getRuntimeSettings().then((settings) => {
      if (!cancelled) {
        setRuntimeSettings(settings);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!eventId || !initialized) return;

    if (!isAuthenticated || !userId) {
      localStorage.setItem('crowdtrades_post_login_redirect', `/events/${eventId}/check-in`);
      navigate('/login');
    }
  }, [eventId, initialized, isAuthenticated, userId, navigate]);

  useEffect(() => {
    if (!eventId || !initialized || !isAuthenticated || !userId) return;

    if (initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;

    let cancelled = false;

    const clearPollingTimeout = () => {
      if (pollingTimeoutRef.current) {
        window.clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };

    const mergeServerState = async (eligibilityData: any, locationData: any, reviews: any[]) => {
      const previousState = eventDayStateRef.current;
      const previousReviewPrompt = previousState.reviewPrompt || {};
      const serverSession = locationData?.session || null;
      const serverAssignment = locationData?.selfReviewAssignment || null;
      const serverSessionId = serverSession?.id || '';
      const sessionAlreadyReviewed = Boolean(serverSessionId && reviews.some((review: any) => review.sessionId === serverSessionId));
      const serverSessionNeedsReview = Boolean(
        serverSessionId
        && serverAssignment
        && ['completed', 'canceled'].includes(serverSession?.status || '')
        && !sessionAlreadyReviewed
      );

      let nextReviewPrompt = {
        sessionId: '',
        session: null,
        assignment: null,
        targetUserId: '',
        targetDisplayName: '',
        submitted: false,
      };

      if (serverSessionNeedsReview) {
        const targetUserId = serverAssignment?.reviewTargetUserId || previousReviewPrompt.targetUserId || '';
        const immediateDisplayName = previousReviewPrompt.targetDisplayName || targetUserId;

        nextReviewPrompt = {
          sessionId: serverSessionId,
          session: serverSession,
          assignment: serverAssignment || previousReviewPrompt.assignment || null,
          targetUserId,
          targetDisplayName: immediateDisplayName,
          submitted: false,
        };

        if (targetUserId && !previousReviewPrompt.targetDisplayName) {
          getCachedUserProfileSummary(targetUserId)
            .then((profile) => {
              if (cancelled) return;
              const resolvedName = profile?.displayName || profile?.userName || targetUserId;
              setEventDayState((current: any) => {
                if (current?.reviewPrompt?.targetUserId !== targetUserId) return current;
                return {
                  ...current,
                  reviewPrompt: {
                    ...(current.reviewPrompt || {}),
                    targetDisplayName: resolvedName,
                  },
                };
              });
            })
            .catch(() => {
              // Keep fallback userId display if profile lookup fails.
            });
        }
      } else {
        const preservedSessionId = previousReviewPrompt.sessionId || '';
        const preservedAlreadyReviewed = Boolean(
          preservedSessionId
          && reviews.some((review: any) => review.sessionId === preservedSessionId)
        );
        const preservedStillPending = Boolean(
          preservedSessionId
          && !preservedAlreadyReviewed
          && !previousReviewPrompt.submitted
        );
        const locationStateStillRelevant = Boolean(locationData?.state === 'active-session' || locationData?.location?.id || locationData?.session?.id);

        if (preservedAlreadyReviewed) {
          nextReviewPrompt = {
            sessionId: '',
            session: null,
            assignment: null,
            targetUserId: '',
            targetDisplayName: '',
            submitted: true,
          };
        } else if (preservedStillPending && locationStateStillRelevant) {
          nextReviewPrompt = {
            ...previousReviewPrompt,
            session: previousReviewPrompt.session || serverSession || null,
            assignment: previousReviewPrompt.assignment || serverAssignment || null,
            targetUserId: previousReviewPrompt.targetUserId || serverAssignment?.reviewTargetUserId || '',
            targetDisplayName: previousReviewPrompt.targetDisplayName || previousReviewPrompt.targetUserId || serverAssignment?.reviewTargetUserId || '',
            submitted: false,
          };
        }
      }

      if (cancelled) return;

      setEventDayState({
        eligibility: eligibilityData,
        locationState: locationData,
        reviews,
        reviewPrompt: nextReviewPrompt,
      });
    };

    const refreshEventDayState = async (silent = true) => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;

      try {
        if (!silent) {
          setLoading(true);
        }
        setError(null);
        const [response, locationResponse, reviewsResponse] = await Promise.all([
          fetch(buildEventsApiUrl(`/api/events/${eventId}/check-in-status/${encodeURIComponent(userId)}`)),
          fetch(buildEventsApiUrl(`/api/events/${eventId}/location-check-in-status/${encodeURIComponent(userId)}`)),
          fetch(buildEventsApiUrl(`/api/events/${eventId}/reviews/${encodeURIComponent(userId)}`)),
        ]);

        const [data, locationData, reviewsData] = await Promise.all([
          response.json(),
          locationResponse.json(),
          reviewsResponse.json(),
        ]);

        if (!response.ok) throw new Error(data.error || 'Failed to load check-in status');
        if (!locationResponse.ok) throw new Error(locationData.error || 'Failed to load location check-in status');
        if (!reviewsResponse.ok) throw new Error(reviewsData.error || 'Failed to load reviews');
        if (cancelled) return;

        const nextEligibility = data.eligibility || null;
        const nextLocationState = locationData || null;
        const nextReviews = Array.isArray(reviewsData.reviews) ? reviewsData.reviews : [];

        if (!silent) {
          setEventDayState((current: any) => ({
            ...current,
            eligibility: nextEligibility,
            locationState: nextLocationState,
            reviews: nextReviews,
          }));
          setLoading(false);
        }

        await mergeServerState(nextEligibility, nextLocationState, nextReviews);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load check-in status');
        }
      } finally {
        refreshInFlightRef.current = false;
        if (!silent) {
          setLoading(false);
        }
      }
    };

    const scheduleNextPoll = () => {
      if (cancelled) return;
      clearPollingTimeout();
      const currentState = eventDayStateRef.current;
      const delayMs = getCheckInPollIntervalMs(currentState.eligibility, currentState.locationState, runtimeSettingsRef.current);
      pollingTimeoutRef.current = window.setTimeout(async () => {
        await refreshEventDayState(true);
        scheduleNextPoll();
      }, delayMs);
    };

    refreshEventDayState(false).then(() => {
      if (!cancelled) {
        scheduleNextPoll();
      }
    });

    return () => {
      cancelled = true;
      clearPollingTimeout();
    };
  }, [eventId, initialized, isAuthenticated, userId, navigate]);

  const eligibility = eventDayState.eligibility;
  const locationState = eventDayState.locationState;
  const existingReviews = eventDayState.reviews || [];
  const reviewPrompt = eventDayState.reviewPrompt || {};
  const reviewTargetUserId = reviewPrompt.targetUserId || '';
  const reviewTargetDisplayName = reviewPrompt.targetDisplayName || reviewTargetUserId;
  const serverReviewAssignment = locationState?.selfReviewAssignment || null;
  const serverReviewSession = locationState?.session || null;
  const reviewAssignment = reviewPrompt.assignment || serverReviewAssignment || null;
  const effectiveReviewSession = reviewPrompt.session || serverReviewSession || null;

  const handleCheckIn = async () => {
    if (!eventId || !userId) return;
    try {
      setSubmitting(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/check-in`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to check in');
      setSuccessMessage('You are checked in and ready for event-day trading.');
      setEventDayState((current: any) => ({
        ...current,
        eligibility: {
          ...(current?.eligibility || {}),
          state: 'already-checked-in',
          participantStatus: data.participantStatus,
          participantSummary: data.participantSummary,
        },
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to check in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    const reviewSessionId = effectiveReviewSession?.id || '';
    const reviewTradeGroupId = effectiveReviewSession?.tradeGroupId;
    const reviewSessionStatus = effectiveReviewSession?.status;
    if (!eventId || !userId || !reviewSessionId || !reviewTargetUserId || !reviewRating) return;
    try {
      setSubmitting(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/reviews`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewerUserId: userId,
          reviewTargetUserId,
          sessionId: reviewSessionId,
          tradeGroupId: reviewTradeGroupId,
          reviewStatus: reviewSessionStatus === 'completed' ? 'completed-session' : 'canceled-session',
          rating: reviewRating,
          comment: reviewComment,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit review');
      setSuccessMessage('Review submitted.');
      setEventDayState((current: any) => ({
        ...current,
        eligibility: {
          ...(current?.eligibility || {}),
          allTradesCompleted: true,
        },
        reviews: [
          ...(current.reviews || []).filter((review: any) => review.sessionId !== reviewSessionId),
          { ...data.review, sessionId: reviewSessionId },
        ],
        locationState: {
          ...(current.locationState || {}),
          state: 'completed-session-reviewed',
          session: current.locationState?.session
            ? {
                ...current.locationState.session,
                status: 'completed',
              }
            : current.locationState?.session,
        },
        reviewPrompt: {
          sessionId: '',
          session: null,
          assignment: null,
          targetUserId: '',
          targetDisplayName: '',
          submitted: true,
        },
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLocationCheckIn = async () => {
    if (!eventId || !userId || !locationState?.location?.id) return;
    try {
      setSubmitting(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/locations/${locationState.location.id}/check-in`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to confirm location presence');
      setSuccessMessage(data.everyonePresent ? 'Everyone is at the table. This trade session is now active.' : 'Your table arrival has been recorded. Waiting for the rest of the group.');
      setEventDayState((current: any) => ({
        ...current,
        locationState: {
          ...current.locationState,
          state: 'active-session',
          checkedInCount: data.checkedInCount,
          totalParticipants: data.totalParticipants,
          selfState: {
            ...(current.locationState?.selfState || {}),
            locationCheckedInAt: new Date().toISOString(),
          },
          session: {
            ...(current.locationState?.session || {}),
            status: data.status,
            startedAt: data.everyonePresent ? new Date().toISOString() : current.locationState?.session?.startedAt,
          },
        },
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to confirm location presence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCompletion = async () => {
    if (!eventId || !userId || !locationState?.session?.id) return;
    try {
      setSubmitting(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/trade-sessions/${locationState.session.id}/complete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to confirm completion');
      setSuccessMessage(data.everyoneConfirmed ? 'Everyone confirmed receipt. This trade session is complete.' : 'Your confirmation was recorded. Waiting for the rest of the group.');
      setEventDayState((current: any) => ({
        ...current,
        locationState: {
          ...current.locationState,
          session: {
            ...(current.locationState?.session || {}),
            status: data.status,
          },
          selfState: {
            ...(current.locationState?.selfState || {}),
            completionConfirmedAt: new Date().toISOString(),
          },
        },
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to confirm completion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportIssue = async () => {
    if (!eventId || !userId || !locationState?.session?.id) return;
    const reasonCode = window.prompt('Issue reason code (example: wrong-item, missing-item, condition-worse-than-expected, other):', 'other');
    if (!reasonCode) return;
    const reasonText = window.prompt('Optional details about the issue:', '') || '';
    try {
      setSubmitting(true);
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/trade-sessions/${locationState.session.id}/report-issue`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, reasonCode, reasonText }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to report issue');
      setSuccessMessage('Issue reported. The trade session has been canceled so the group can be handled safely.');
      setEventDayState((current: any) => ({
        ...current,
        locationState: {
          ...current.locationState,
          session: {
            ...(current.locationState?.session || {}),
            status: 'canceled',
          },
          selfState: {
            ...(current.locationState?.selfState || {}),
            issueReportedAt: new Date().toISOString(),
          },
        },
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to report issue');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    const state = eligibility?.state;
    const eventName = eligibility?.event?.eventName || 'this event';
    const matchedTrades = Array.isArray(eligibility?.participantSummary?.matched) ? eligibility.participantSummary.matched : [];
    const reviewedSessionIds = new Set(existingReviews.map((review: any) => review.sessionId).filter(Boolean));
    const allMatchedTradesComplete = Boolean(eligibility?.allTradesCompleted) || (matchedTrades.length > 0 && reviewedSessionIds.size >= matchedTrades.length);
    const liveSessionAwaitingReview = Boolean(
      locationState?.state === 'completed-session-awaiting-review'
      && serverReviewSession?.id
      && serverReviewAssignment
      && ['completed', 'canceled'].includes(serverReviewSession?.status || '')
      && !existingReviews.some((review: any) => review.sessionId === serverReviewSession.id)
    );
    const shouldShowReviewPrompt = Boolean(
      liveSessionAwaitingReview || (
        reviewPrompt.sessionId
        && !reviewPrompt.submitted
        && !existingReviews.some((review: any) => review.sessionId === reviewPrompt.sessionId)
      )
    );

    let phase: 'completed' | 'pending-review' | 'active-session' | 'waiting' = 'waiting';
    if (liveSessionAwaitingReview || shouldShowReviewPrompt) phase = 'pending-review';
    else if (locationState?.state === 'active-session') phase = 'active-session';
    else if (allMatchedTradesComplete && (!locationState?.session?.status || ['completed', 'canceled'].includes(locationState?.session?.status))) phase = 'completed';

    switch (state) {
      case 'event-inactive':
        return <Alert severity="info">{eventName} is not currently active, so check-in is not available.</Alert>;
      case 'cutoff-not-passed':
        return <Alert severity="info">Check-in is not available yet because the trade cutoff has not passed.</Alert>;
      case 'results-not-ready':
        return <Alert severity="info">Trade results are not ready yet for {eventName}.</Alert>;
      case 'not-participant':
        return <Alert severity="warning">You are signed in, but you are not registered as a participant for this event.</Alert>;
      case 'no-trades':
        return <Alert severity="info">You participated in this event, but you were not matched in any trades, so no check-in is needed.</Alert>;
      case 'already-checked-in':
        return (
          <Stack spacing={2}>
            <Alert severity="success">{successMessage || 'You are already checked in for this event.'}</Alert>
            {eligibility?.participantSummary?.matched?.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                You are involved in {eligibility.participantSummary.matched.length} trade{eligibility.participantSummary.matched.length === 1 ? '' : 's'}.
              </Typography>
            )}

            {phase === 'completed' && <CompletedAllTradesPanel />}
            {phase === 'waiting' && <WaitingForAssignmentPanel submitting={submitting} />}
            {phase === 'active-session' && (
              <TradeSessionPanel
                locationState={locationState}
                submitting={submitting}
                onLocationCheckIn={handleLocationCheckIn}
                onConfirmCompletion={handleConfirmCompletion}
                onReportIssue={handleReportIssue}
              />
            )}
            {phase === 'pending-review' && (
              <PostTradeReviewPanel
                reviewTargetDisplayName={reviewTargetDisplayName || serverReviewAssignment?.reviewTargetUserId || ''}
                reviewTargetUserId={reviewTargetUserId || serverReviewAssignment?.reviewTargetUserId || ''}
                reviewAssignment={reviewAssignment}
                reviewRating={reviewRating}
                reviewComment={reviewComment}
                submitting={submitting}
                canSubmit={Boolean(reviewTargetUserId && reviewRating && effectiveReviewSession?.id)}
                onRatingChange={setReviewRating}
                onCommentChange={setReviewComment}
                onSubmit={handleSubmitReview}
                onViewProfile={() => navigate(`/users/${reviewTargetUserId}`)}
              />
            )}
          </Stack>
        );
      case 'eligible':
        return (
          <Stack spacing={2}>
            <Alert severity="success">You are eligible to check in for trade fulfillment.</Alert>
            {eligibility?.participantSummary?.matched?.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                You are involved in {eligibility.participantSummary.matched.length} trade{eligibility.participantSummary.matched.length === 1 ? '' : 's'} in the finalized results.
              </Typography>
            )}
            <Button variant="contained" size="large" onClick={handleCheckIn} disabled={submitting}>
              {submitting ? 'Checking In...' : 'Check In'}
            </Button>
          </Stack>
        );
      default:
        return <Alert severity="info">Loading event-day check-in status...</Alert>;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Event Check-In</Typography>
          <Typography variant="body2" color="text.secondary">
            Scan the event QR code to check in for trade fulfillment. If you are signed in and eligible, you can confirm your arrival here.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fbff' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Need the event-day walkthrough?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The guide explains event QR check-in, assigned trade locations, completion, issue reporting, and what admins are doing behind the scenes.
              </Typography>
              <Box>
                <Button variant="outlined" onClick={() => navigate('/event-day-guide')}>
                  Open Event-Day Guide
                </Button>
              </Box>
            </Stack>
          </Paper>
          {error && <Alert severity="error">{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : renderBody()}
          <Button variant="text" onClick={() => navigate(eventId ? `/events/${eventId}` : '/')}>Back to Event</Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default EventCheckInPage;
